import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  of,
  tap,
} from 'rxjs';
import { GeneralService } from './general.service';
import { API } from '../configs/api.config';
import {
  MarkAsReadRequest,
  MarkAsReadResponse,
  NotificationDashboardHistoriesRequest,
  NotificationDashboardHistoriesResponse,
  NotificationDisplayRequest,
  NotificationDisplayResponse,
  NotificationDisplaySeenRequest,
  NotificationDisplaySeenResponse,
} from '../models/notification.model';
import { actionDefault } from '../configs/http-headers.config';
import { LS } from '@shared/enums/local-storage.enum';

interface NotificationDisplayState {
  data: NotificationDisplayResponse | null;
  loading: boolean;
  error: string | null;
}

const initialDisplayState: NotificationDisplayState = {
  data: null,
  loading: false,
  error: null,
};

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly displayKey = LS.NOTIFICATION_DISPLAY;
  private readonly displayState = new BehaviorSubject<NotificationDisplayState>(
    initialDisplayState,
  );
  readonly displayState$ = this.displayState.asObservable();

  private readonly _notificationRead$ = new Subject<number>();
  readonly notificationRead$ = this._notificationRead$.asObservable();

  actionId = actionDefault;
  menuId = '-';
  constructor(private readonly generalService: GeneralService) {
    this.rehydrateDisplayState();
  }

  getDashboardHistories(
    params: NotificationDashboardHistoriesRequest,
  ): Observable<NotificationDashboardHistoriesResponse> {
    return this.generalService.postData(
      params,
      API.NOTIFICATION_HISTORY,
      API.NOTIFICATION_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  markAsRead(params: MarkAsReadRequest): Observable<MarkAsReadResponse> {
    return this.generalService.postData(
      params,
      API.NOTIFICATION_HISTORY_READ,
      API.NOTIFICATION_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  /**
   * Get notification display settings for the current merchant
   * @param params - Request parameters containing imid
   * @param useCache - Whether to check cache before making the HTTP request
   * @returns Observable with notification display response containing flags
   */
  getDisplay(
    params: NotificationDisplayRequest,
    useCache = true,
    menuId?: string,
  ): Observable<NotificationDisplayResponse> {
    if (useCache && this.displayState.value.data) {
      return of(this.displayState.value.data);
    }

    this.displayState.next({ ...initialDisplayState, loading: true });
    return this.generalService
      .postData(
        params,
        API.NOTIFICATION_DISPLAY,
        API.NOTIFICATION_BASE_URL,
        menuId || '-',
        '-',
      )
      .pipe(
        tap((res: NotificationDisplayResponse) => {
          this.setDisplayState(res);
        }),
        catchError((error: Error) => this.handleDisplayError(error)),
      );
  }

  /**
   * Mark notification display as seen
   * @param params - Request parameters containing imid and displayType
   * @returns Observable with notification display seen response
   */
  markDisplayAsSeen(
    params: NotificationDisplaySeenRequest,
  ): Observable<NotificationDisplaySeenResponse> {
    return this.generalService.postData(
      params,
      API.NOTIFICATION_DISPLAY_SEEN,
      API.NOTIFICATION_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  emitNotificationRead(readCount: number): void {
    this._notificationRead$.next(readCount);
  }

  markAllAsRead(): Observable<MarkAsReadResponse> {
    const menuId = '39293c7a-1e38-4a9a-9ea9-b00d454ed927';
    return this.generalService.postData(
      {},
      API.NOTIFICATION_HISTORY_READ_ALL,
      API.NOTIFICATION_BASE_URL,
      menuId,
      this.actionId,
    );
  }

  /**
   * Clears cached notification display state.
   */
  clearDisplayState(): void {
    this.displayState.next(initialDisplayState);
    try {
      localStorage.removeItem(this.displayKey);
    } catch (error) {
      console.error('Failed to clear notification display state:', error);
    }
  }

  private rehydrateDisplayState(): void {
    try {
      const cached = localStorage.getItem(this.displayKey);
      if (cached) {
        const data: NotificationDisplayResponse = JSON.parse(cached);
        this.displayState.next({
          data,
          loading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error('Failed to rehydrate notification display state:', error);
    }
  }

  private setDisplayState(data: NotificationDisplayResponse): void {
    this.displayState.next({
      data,
      loading: false,
      error: null,
    });
    try {
      localStorage.setItem(this.displayKey, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to cache notification display data:', error);
    }
  }

  private handleDisplayError(error: Error): Observable<never> {
    this.displayState.next({
      data: null,
      loading: false,
      error: error.message,
    });
    return EMPTY;
  }
}
